/**
 * ============================================
 * HORIZONTAL MOON MAP - 3D CANVAS MOCKUP
 * Interactive Three.js 3D preview showcase
 * 
 * This code is proprietary.
 * Unauthorized copying, modification, distribution,
 * or use of this software is strictly prohibited.
 * 
 * Copyright (c) 2025 Skylit Studio
 * All rights reserved.
 * ============================================
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    canvasWidth: 800,
    canvasHeight: 600,
    frameDepth: 0.04,        // Canvas depth (thickness) - thinner for realistic look
    frameAspect: 3 / 2,      // Landscape aspect ratio
    initialRotationY: -0.3,  // Initial Y rotation (radians)
    initialRotationX: 0.1,   // Initial X rotation
    rotationSpeed: 0.005,    // Auto-rotation speed
    dragSensitivity: 0.005,  // Mouse drag sensitivity
    ambientLightIntensity: 0.6,
    directionalLightIntensity: 0.8,
    backgroundColor: null,  // Transparent background to show wood wall
    wrapEdgeSize: 0.03,      // Size of gallery wrap edge (percentage of canvas)
    // Zoom settings
    initialZoom: 2.5,        // Initial camera distance
    minZoom: 1.5,            // Closest zoom (minimum camera distance)
    maxZoom: 5.0,            // Furthest zoom (maximum camera distance)
    zoomSpeed: 0.15          // Zoom sensitivity for scroll wheel
  };

  // State
  let mockupState = {
    scene: null,
    camera: null,
    renderer: null,
    canvasMesh: null,
    frameGroup: null,
    isActive: false,
    isDragging: false,
    previousMouseX: 0,
    previousMouseY: 0,
    targetRotationY: CONFIG.initialRotationY,
    targetRotationX: CONFIG.initialRotationX,
    animationId: null,
    autoRotate: true,
    // Zoom state
    currentZoom: CONFIG.initialZoom,
    targetZoom: CONFIG.initialZoom,
    // Pinch zoom state
    isPinching: false,
    initialPinchDistance: 0,
    // Live sync state
    mutationObserver: null,
    inputListeners: [],
    updateDebounceTimer: null
  };

  /**
   * Initialize the 3D mockup system
   */
  function init3DMockup() {
    // Create container if it doesn't exist
    let container = document.getElementById('h-3d-mockup-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'h-3d-mockup-container';
      container.className = 'h-3d-mockup-container';

      // Add title at top
      const title = document.createElement('div');
      title.className = 'h-3d-title';
      title.innerHTML = 'Gallery Wrap Canvas Preview';
      container.appendChild(title);

      // Add instructions overlay
      const instructions = document.createElement('div');
      instructions.className = 'h-3d-instructions';
      instructions.innerHTML = 'Drag to rotate &bull; Pinch or scroll to zoom &bull; Double-tap to reset';
      container.appendChild(instructions);

      // Insert inside .h-app__main for proper positioning
      const mainArea = document.querySelector('.h-app__main');
      if (mainArea) {
        mainArea.appendChild(container);
      } else {
        // Fallback: Insert after #h-print
        const printArea = document.getElementById('h-print');
        if (printArea && printArea.parentNode) {
          printArea.parentNode.insertBefore(container, printArea.nextSibling);
        }
      }
    }

    // Create canvas element for Three.js
    const canvas = document.createElement('canvas');
    canvas.id = 'h-3d-mockup-canvas';
    canvas.className = 'h-3d-mockup-canvas';
    container.appendChild(canvas);

    // Initialize Three.js scene
    initThreeJSScene(canvas, container);

    // Add event listeners
    addEventListeners(canvas);

    console.log('3D Mockup initialized');
  }

  /**
   * Initialize Three.js scene, camera, renderer
   */
  function initThreeJSScene(canvas, container) {
    // Scene
    mockupState.scene = new THREE.Scene();
    mockupState.scene.background = CONFIG.backgroundColor ? new THREE.Color(CONFIG.backgroundColor) : null;

    // Camera
    const aspect = CONFIG.canvasWidth / CONFIG.canvasHeight;
    mockupState.camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 1000);
    mockupState.camera.position.set(0, 0, 2.5);
    mockupState.camera.lookAt(0, 0, 0);

    // Renderer
    mockupState.renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      antialias: true,
      alpha: true
    });
    mockupState.renderer.setClearColor(0x000000, 0); // Transparent clear color
    mockupState.renderer.setSize(CONFIG.canvasWidth, CONFIG.canvasHeight);
    mockupState.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mockupState.renderer.shadowMap.enabled = true;
    mockupState.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mockupState.renderer.outputColorSpace = THREE.SRGBColorSpace; // Match color space

    // Lighting
    addLighting();

    // Create initial canvas frame (will be updated with texture later)
    createCanvasFrame();
  }

  /**
   * Add lighting to the scene
   */
  function addLighting() {
    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, CONFIG.ambientLightIntensity);
    mockupState.scene.add(ambientLight);

    // Main directional light (front-top-right)
    const mainLight = new THREE.DirectionalLight(0xffffff, CONFIG.directionalLightIntensity);
    mainLight.position.set(2, 3, 4);
    mainLight.castShadow = true;
    mainLight.shadow.mapSize.width = 1024;
    mainLight.shadow.mapSize.height = 1024;
    mockupState.scene.add(mainLight);

    // Fill light (front-left)
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
    fillLight.position.set(-2, 1, 3);
    mockupState.scene.add(fillLight);

    // Back rim light for depth
    const rimLight = new THREE.DirectionalLight(0x6699ff, 0.2);
    rimLight.position.set(0, 2, -3);
    mockupState.scene.add(rimLight);
  }

  /**
   * Create a rounded box geometry with slightly beveled edges
   * Uses ExtrudeGeometry with a rounded rectangle shape for softer wood appearance
   */
  function createRoundedBoxGeometry(width, height, depth, radius) {
    // Clamp radius to reasonable bounds
    const r = Math.min(radius, width / 2, height / 2);

    // Create rounded rectangle shape
    const shape = new THREE.Shape();
    const x = -width / 2;
    const y = -height / 2;

    shape.moveTo(x + r, y);
    shape.lineTo(x + width - r, y);
    shape.quadraticCurveTo(x + width, y, x + width, y + r);
    shape.lineTo(x + width, y + height - r);
    shape.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    shape.lineTo(x + r, y + height);
    shape.quadraticCurveTo(x, y + height, x, y + height - r);
    shape.lineTo(x, y + r);
    shape.quadraticCurveTo(x, y, x + r, y);

    // Extrude settings with slight bevel for softer edges
    const extrudeSettings = {
      depth: depth,
      bevelEnabled: true,
      bevelThickness: r * 0.3,
      bevelSize: r * 0.3,
      bevelSegments: 2
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    // Center the geometry on z-axis
    geometry.translate(0, 0, -depth / 2);

    return geometry;
  }

  /**
   * Create a procedural canvas weave texture for bump mapping
   * Creates a fine tileable pattern that simulates woven canvas fabric
   */
  function createCanvasWeaveTexture() {
    const size = 64; // Smaller texture for finer detail
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    // Base color (mid-gray for bump map)
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, size, size);

    // Create fine weave pattern
    const weaveSize = 2; // Tiny weave units

    for (let y = 0; y < size; y += weaveSize) {
      for (let x = 0; x < size; x += weaveSize) {
        // Alternate pattern for woven look
        const isEvenRow = Math.floor(y / weaveSize) % 2 === 0;
        const isEvenCol = Math.floor(x / weaveSize) % 2 === 0;

        // Subtle height variations
        let brightness;
        if (isEvenRow === isEvenCol) {
          brightness = 135 + Math.random() * 10; // Slightly raised
        } else {
          brightness = 120 + Math.random() * 10; // Slightly recessed
        }

        ctx.fillStyle = `rgb(${brightness}, ${brightness}, ${brightness})`;
        ctx.fillRect(x, y, weaveSize, weaveSize);
      }
    }

    // Add fine noise for natural canvas variation
    const imageData = ctx.getImageData(0, 0, size, size);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const noise = (Math.random() - 0.5) * 8;
      data[i] = Math.max(0, Math.min(255, data[i] + noise));
      data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise));
      data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise));
    }
    ctx.putImageData(imageData, 0, 0);

    const bumpTexture = new THREE.CanvasTexture(canvas);
    bumpTexture.wrapS = THREE.RepeatWrapping;
    bumpTexture.wrapT = THREE.RepeatWrapping;
    bumpTexture.repeat.set(80, 55); // Many tiny repeats across canvas

    return bumpTexture;
  }

  /**
   * Create the 3D canvas frame geometry with gallery wrap support
   */
  function createCanvasFrame(texture = null, edgeTextures = null) {
    // Preserve current rotation if this is an update (frame already exists)
    let preserveRotationY = null;
    let preserveRotationX = null;
    const isUpdate = mockupState.frameGroup !== null;

    if (isUpdate) {
      preserveRotationY = mockupState.frameGroup.rotation.y;
      preserveRotationX = mockupState.frameGroup.rotation.x;
    }

    // Remove existing frame group if present
    if (mockupState.frameGroup) {
      mockupState.scene.remove(mockupState.frameGroup);
    }

    mockupState.frameGroup = new THREE.Group();

    const width = 1.5;
    const height = width / CONFIG.frameAspect;
    const depth = CONFIG.frameDepth;
    const canvasWrapWidth = 0.028; // Width of canvas wrap on back edges (increased for more visible wrap)

    // Wood material for stretcher bars (light pine color like reference)
    const woodMaterial = new THREE.MeshStandardMaterial({
      color: 0xD4B896,  // Light pine/birch color
      roughness: 0.8,
      metalness: 0.0
    });

    // White canvas wrap material (back of canvas paper)
    const canvasWrapMaterial = new THREE.MeshStandardMaterial({
      color: 0xFAFAFA,  // Off-white canvas
      roughness: 0.85,
      metalness: 0.0,
      side: THREE.DoubleSide
    });

    // Default side material (fallback)
    const defaultSideMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      roughness: 0.8,
      metalness: 0.1,
      side: THREE.DoubleSide
    });

    // Create side materials - use edge textures for gallery wrap
    // DoubleSide ensures visibility from all angles
    let rightMaterial = defaultSideMaterial;
    let leftMaterial = defaultSideMaterial;
    let topMaterial = defaultSideMaterial;
    let bottomMaterial = defaultSideMaterial;

    if (edgeTextures) {
      if (edgeTextures.right) {
        rightMaterial = new THREE.MeshStandardMaterial({
          map: edgeTextures.right,
          roughness: 0.85,  // Matte canvas
          metalness: 0,
          side: THREE.DoubleSide
        });
      }
      if (edgeTextures.left) {
        leftMaterial = new THREE.MeshStandardMaterial({
          map: edgeTextures.left,
          roughness: 0.85,  // Matte canvas
          metalness: 0,
          side: THREE.DoubleSide
        });
      }
      if (edgeTextures.top) {
        topMaterial = new THREE.MeshStandardMaterial({
          map: edgeTextures.top,
          roughness: 0.85,  // Matte canvas
          metalness: 0,
          side: THREE.DoubleSide
        });
      }
      if (edgeTextures.bottom) {
        bottomMaterial = new THREE.MeshStandardMaterial({
          map: edgeTextures.bottom,
          roughness: 0.85,  // Matte canvas
          metalness: 0,
          side: THREE.DoubleSide
        });
      }
    }

    // Create canvas weave bump texture for realistic fabric look
    const canvasBumpTexture = createCanvasWeaveTexture();

    // Front face material - matte canvas texture with subtle weave bump
    let frontMaterial;
    if (texture) {
      frontMaterial = new THREE.MeshStandardMaterial({
        map: texture,
        bumpMap: canvasBumpTexture,
        bumpScale: 0.003,  // Very subtle canvas texture
        roughness: 0.9,    // High roughness for matte canvas look
        metalness: 0
      });
    } else {
      frontMaterial = new THREE.MeshStandardMaterial({
        color: 0x111122,
        bumpMap: canvasBumpTexture,
        bumpScale: 0.003,
        roughness: 0.9,
        metalness: 0
      });
    }

    // === FRONT FACE (the print) ===
    const frontGeometry = new THREE.PlaneGeometry(width, height);
    const frontMesh = new THREE.Mesh(frontGeometry, frontMaterial);
    frontMesh.position.z = depth / 2;
    frontMesh.castShadow = true;
    frontMesh.receiveShadow = true;
    mockupState.frameGroup.add(frontMesh);

    // === SIDE EDGES with gallery wrap ===
    // Right side
    const rightGeometry = new THREE.PlaneGeometry(depth, height);
    const rightMesh = new THREE.Mesh(rightGeometry, rightMaterial);
    rightMesh.rotation.y = -Math.PI / 2;
    rightMesh.position.x = width / 2;
    rightMesh.position.z = 0;
    mockupState.frameGroup.add(rightMesh);

    // Left side
    const leftGeometry = new THREE.PlaneGeometry(depth, height);
    const leftMesh = new THREE.Mesh(leftGeometry, leftMaterial);
    leftMesh.rotation.y = Math.PI / 2;
    leftMesh.position.x = -width / 2;
    leftMesh.position.z = 0;
    mockupState.frameGroup.add(leftMesh);

    // Top side
    const topGeometry = new THREE.PlaneGeometry(width, depth);
    const topMesh = new THREE.Mesh(topGeometry, topMaterial);
    topMesh.rotation.x = Math.PI / 2;
    topMesh.position.y = height / 2;
    topMesh.position.z = 0;
    mockupState.frameGroup.add(topMesh);

    // Bottom side
    const bottomGeometry = new THREE.PlaneGeometry(width, depth);
    const bottomMesh = new THREE.Mesh(bottomGeometry, bottomMaterial);
    bottomMesh.rotation.x = -Math.PI / 2;
    bottomMesh.position.y = -height / 2;
    bottomMesh.position.z = 0;
    mockupState.frameGroup.add(bottomMesh);

    // === CANVAS WRAP ON BACK EDGES (continuation of image texture wrapped onto back) ===
    // Create back wrap materials - use edge textures if available, otherwise fallback to white
    let topBackWrapMaterial = canvasWrapMaterial;
    let bottomBackWrapMaterial = canvasWrapMaterial;
    let leftBackWrapMaterial = canvasWrapMaterial;
    let rightBackWrapMaterial = canvasWrapMaterial;

    if (edgeTextures) {
      // Helper to flip texture vertically for back wraps
      function flipTextureVertically(texture) {
        const flipped = texture.clone();
        flipped.repeat.y = -1;
        flipped.offset.y = 1;
        flipped.needsUpdate = true;
        return flipped;
      }

      if (edgeTextures.top) {
        topBackWrapMaterial = new THREE.MeshStandardMaterial({
          map: flipTextureVertically(edgeTextures.top),
          roughness: 0.85,
          metalness: 0,
          side: THREE.DoubleSide
        });
      }
      if (edgeTextures.bottom) {
        bottomBackWrapMaterial = new THREE.MeshStandardMaterial({
          map: flipTextureVertically(edgeTextures.bottom),
          roughness: 0.85,
          metalness: 0,
          side: THREE.DoubleSide
        });
      }
      if (edgeTextures.left) {
        leftBackWrapMaterial = new THREE.MeshStandardMaterial({
          map: flipTextureVertically(edgeTextures.left),
          roughness: 0.85,
          metalness: 0,
          side: THREE.DoubleSide
        });
      }
      if (edgeTextures.right) {
        rightBackWrapMaterial = new THREE.MeshStandardMaterial({
          map: flipTextureVertically(edgeTextures.right),
          roughness: 0.85,
          metalness: 0,
          side: THREE.DoubleSide
        });
      }
    }

    // Top back wrap
    const topWrapGeo = new THREE.PlaneGeometry(width, canvasWrapWidth);
    const topWrap = new THREE.Mesh(topWrapGeo, topBackWrapMaterial);
    topWrap.rotation.x = Math.PI;
    topWrap.position.set(0, height / 2 - canvasWrapWidth / 2, -depth / 2);
    mockupState.frameGroup.add(topWrap);

    // Bottom back wrap
    const bottomWrap = new THREE.Mesh(topWrapGeo, bottomBackWrapMaterial);
    bottomWrap.rotation.x = Math.PI;
    bottomWrap.position.set(0, -height / 2 + canvasWrapWidth / 2, -depth / 2);
    mockupState.frameGroup.add(bottomWrap);

    // Left back wrap
    const sideWrapGeo = new THREE.PlaneGeometry(canvasWrapWidth, height - canvasWrapWidth * 2);
    const leftWrap = new THREE.Mesh(sideWrapGeo, leftBackWrapMaterial);
    leftWrap.rotation.x = Math.PI;
    leftWrap.position.set(-width / 2 + canvasWrapWidth / 2, 0, -depth / 2);
    mockupState.frameGroup.add(leftWrap);

    // Right back wrap
    const rightWrap = new THREE.Mesh(sideWrapGeo, rightBackWrapMaterial);
    rightWrap.rotation.x = Math.PI;
    rightWrap.position.set(width / 2 - canvasWrapWidth / 2, 0, -depth / 2);
    mockupState.frameGroup.add(rightWrap);

    // === WOODEN STRETCHER BARS (visible through hollow back) ===
    const stretcherBarWidth = 0.032; // Increased for thicker/wider wood appearance
    const stretcherBarDepth = depth * 0.85;
    const stretcherInset = canvasWrapWidth + 0.002;
    const woodCornerRadius = 0.004; // Subtle rounded corners for softer wood appearance

    // Top stretcher bar (rounded edges)
    const topBarGeo = createRoundedBoxGeometry(width - stretcherInset * 2, stretcherBarWidth, stretcherBarDepth, woodCornerRadius);
    const topBar = new THREE.Mesh(topBarGeo, woodMaterial);
    topBar.position.set(0, height / 2 - stretcherInset - stretcherBarWidth / 2, 0);
    topBar.castShadow = true;
    mockupState.frameGroup.add(topBar);

    // Bottom stretcher bar (rounded edges)
    const bottomBarGeo = createRoundedBoxGeometry(width - stretcherInset * 2, stretcherBarWidth, stretcherBarDepth, woodCornerRadius);
    const bottomBar = new THREE.Mesh(bottomBarGeo, woodMaterial);
    bottomBar.position.set(0, -height / 2 + stretcherInset + stretcherBarWidth / 2, 0);
    bottomBar.castShadow = true;
    mockupState.frameGroup.add(bottomBar);

    // Left stretcher bar (rounded edges)
    const sideBarGeo = createRoundedBoxGeometry(stretcherBarWidth, height - stretcherInset * 2 - stretcherBarWidth * 2, stretcherBarDepth, woodCornerRadius);
    const leftBar = new THREE.Mesh(sideBarGeo, woodMaterial);
    leftBar.position.set(-width / 2 + stretcherInset + stretcherBarWidth / 2, 0, 0);
    leftBar.castShadow = true;
    mockupState.frameGroup.add(leftBar);

    // Right stretcher bar (rounded edges)
    const rightBarGeo = createRoundedBoxGeometry(stretcherBarWidth, height - stretcherInset * 2 - stretcherBarWidth * 2, stretcherBarDepth, woodCornerRadius);
    const rightBar = new THREE.Mesh(rightBarGeo, woodMaterial);
    rightBar.position.set(width / 2 - stretcherInset - stretcherBarWidth / 2, 0, 0);
    rightBar.castShadow = true;
    mockupState.frameGroup.add(rightBar);

    // === INNER CANVAS BACK (white canvas visible inside the hollow) ===
    const innerCanvasInset = stretcherInset + stretcherBarWidth;
    const innerCanvasGeo = new THREE.PlaneGeometry(
      width - innerCanvasInset * 2,
      height - innerCanvasInset * 2
    );
    const innerCanvas = new THREE.Mesh(innerCanvasGeo, canvasWrapMaterial);
    innerCanvas.rotation.y = Math.PI; // Face backwards
    innerCanvas.position.z = depth / 2 - 0.002; // Just behind front face
    mockupState.frameGroup.add(innerCanvas);

    // === STICKER on back of canvas (bottom right) ===
    const stickerSize = 0.12; // Size of the sticker
    const stickerGeo = new THREE.PlaneGeometry(stickerSize, stickerSize);
    const textureLoader = new THREE.TextureLoader();
    const stickerTexture = textureLoader.load(
      'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/UNFORGETTABLE-STICKER.webp?v=1765329354',
      function(tex) {
        tex.colorSpace = THREE.SRGBColorSpace;
        console.log('3D Mockup: Sticker texture loaded');
      }
    );
    const stickerMaterial = new THREE.MeshStandardMaterial({
      map: stickerTexture,
      transparent: true,
      roughness: 0.3,
      metalness: 0.1,
      side: THREE.DoubleSide
    });
    const stickerMesh = new THREE.Mesh(stickerGeo, stickerMaterial);
    stickerMesh.rotation.y = Math.PI; // Face backwards (same as inner canvas)
    // Position in bottom right of inner canvas area
    const innerWidth = width - innerCanvasInset * 2;
    const innerHeight = height - innerCanvasInset * 2;
    stickerMesh.position.set(
      innerWidth / 2 - stickerSize / 2 - 0.02,  // Right side with small margin
      -innerHeight / 2 + stickerSize / 2 + 0.02, // Bottom with small margin
      depth / 2 - 0.003 // Slightly in front of inner canvas
    );
    mockupState.frameGroup.add(stickerMesh);

    // Store reference for material updates
    mockupState.canvasMesh = frontMesh;

    // Set rotation - preserve current rotation if updating, otherwise use initial
    if (isUpdate && preserveRotationY !== null) {
      mockupState.frameGroup.rotation.y = preserveRotationY;
      mockupState.frameGroup.rotation.x = preserveRotationX;
      // Also sync target rotation to prevent animation fighting the preserved rotation
      mockupState.targetRotationY = preserveRotationY;
      mockupState.targetRotationX = preserveRotationX;
    } else {
      mockupState.frameGroup.rotation.y = CONFIG.initialRotationY;
      mockupState.frameGroup.rotation.x = CONFIG.initialRotationX;
    }

    mockupState.scene.add(mockupState.frameGroup);
  }

  /**
   * Add subtle edge details to make it look like a stretched canvas
   */
  function addCanvasEdgeDetail(width, height, depth) {
    // Skip - now handled by the new hollow frame construction
  }

  /**
   * Convert a single WebGL canvas to a 2D canvas copy
   */
  async function convertWebGLTo2D(webglCanvas) {
    return new Promise((resolve, reject) => {
      try {
        let renderedMoon = false;

        // First, force a render of this specific moon canvas
        // Moon instances are stored in window.hMoonInstances by hor-moon-generator.js
        if (window.hMoonInstances) {
          Object.keys(window.hMoonInstances).forEach(function(key) {
            const instance = window.hMoonInstances[key];
            // Check if this instance owns this canvas and is initialized
            if (instance && instance.initialized && instance.renderer &&
                instance.renderer.domElement === webglCanvas &&
                instance.scene && instance.camera) {
              // Force render right before capture
              instance.renderer.render(instance.scene, instance.camera);
              console.log('3D Mockup: Forced render for moon', key);
              renderedMoon = true;
            }
          });
        }

        // Also try matching by canvas ID (h-moon-canvas-1, h-moon-canvas-2, etc.)
        if (!renderedMoon && window.hMoonInstances) {
          const canvasId = webglCanvas.id;
          if (canvasId && canvasId.startsWith('h-moon-canvas-')) {
            const moonNum = canvasId.replace('h-moon-canvas-', '');
            const instance = window.hMoonInstances[moonNum];
            if (instance && instance.initialized && instance.renderer &&
                instance.scene && instance.camera) {
              instance.renderer.render(instance.scene, instance.camera);
              console.log('3D Mockup: Forced render for moon (by ID)', moonNum);
              renderedMoon = true;
            }
          }
        }

        // Use toDataURL immediately after render to capture the WebGL content
        const dataURL = webglCanvas.toDataURL('image/png');
        console.log('3D Mockup: Canvas captured, dataURL length:', dataURL.length);

        // Create a 2D canvas copy with the same dimensions
        const copy2D = document.createElement('canvas');
        copy2D.width = webglCanvas.width || 512;
        copy2D.height = webglCanvas.height || 512;
        copy2D.style.cssText = webglCanvas.style.cssText;
        copy2D.className = webglCanvas.className;

        const ctx = copy2D.getContext('2d');

        // Load the dataURL as an image and draw it
        const img = new Image();
        img.onload = function() {
          ctx.drawImage(img, 0, 0, copy2D.width, copy2D.height);
          console.log('3D Mockup: Image drawn to 2D canvas');
          resolve({
            original: webglCanvas,
            replacement: copy2D,
            parent: webglCanvas.parentNode
          });
        };
        img.onerror = function() {
          console.warn('3D Mockup: Failed to load WebGL canvas as image');
          reject(new Error('Image load failed'));
        };
        img.src = dataURL;

      } catch (e) {
        console.warn('3D Mockup: Could not convert WebGL canvas:', e);
        reject(e);
      }
    });
  }

  /**
   * Pre-load an image and return it
   */
  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image: ' + src));
      img.src = src;
    });
  }

  /**
   * Capture the current preview using manual canvas drawing
   * This bypasses html2canvas/dom-to-image issues by drawing elements directly
   */
  async function captureAndUpdateTexture() {
    const frame = document.querySelector('.h-custom-frame');
    if (!frame) {
      console.warn('Could not find .h-custom-frame to capture');
      return;
    }

    try {
      // Get the frame's actual dimensions
      const frameRect = frame.getBoundingClientRect();
      const scale = 2; // For higher quality
      const canvasWidth = frameRect.width * scale;
      const canvasHeight = frameRect.height * scale;

      console.log('3D Mockup: Creating manual canvas', canvasWidth, 'x', canvasHeight);

      // Create final canvas
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = canvasWidth;
      finalCanvas.height = canvasHeight;
      const ctx = finalCanvas.getContext('2d');

      // Scale context for high DPI
      ctx.scale(scale, scale);

      // Get computed styles early - needed for background and text
      const frameStyles = window.getComputedStyle(frame);
      const textColor = frameStyles.color || '#ffffff';

      // 1. Draw background image - get URL dynamically from CSS
      let bgImageUrl = 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-personalised-moon-phases-family-background3.webp?v=1765254076';

      // Try to get the actual background image from the frame's computed style
      const computedBg = frameStyles.backgroundImage;
      if (computedBg && computedBg !== 'none') {
        // Extract URL from "url("...")" format
        const urlMatch = computedBg.match(/url\(['"]?([^'"]+)['"]?\)/);
        if (urlMatch && urlMatch[1]) {
          bgImageUrl = urlMatch[1];
          console.log('3D Mockup: Using dynamic background:', bgImageUrl);
        }
      }

      try {
        const bgImage = await loadImage(bgImageUrl);
        // Cover fit calculation
        const imgRatio = bgImage.width / bgImage.height;
        const canvasRatio = frameRect.width / frameRect.height;
        let drawWidth, drawHeight, drawX, drawY;

        if (imgRatio > canvasRatio) {
          drawHeight = frameRect.height;
          drawWidth = drawHeight * imgRatio;
          drawX = (frameRect.width - drawWidth) / 2;
          drawY = 0;
        } else {
          drawWidth = frameRect.width;
          drawHeight = drawWidth / imgRatio;
          drawX = 0;
          drawY = (frameRect.height - drawHeight) / 2;
        }
        ctx.drawImage(bgImage, drawX, drawY, drawWidth, drawHeight);
        console.log('3D Mockup: Background drawn');
      } catch (e) {
        // Fallback gradient
        ctx.fillStyle = '#0a1628';
        ctx.fillRect(0, 0, frameRect.width, frameRect.height);
        console.warn('3D Mockup: Using fallback background');
      }

      // 2. Get layout info from DOM - use actual element positions

      // Helper to get element position relative to frame
      function getRelativePosition(el) {
        const elRect = el.getBoundingClientRect();
        return {
          x: elRect.left - frameRect.left,
          y: elRect.top - frameRect.top,
          width: elRect.width,
          height: elRect.height
        };
      }

      // 3. Draw title - #h-skyMap-title
      const titleEl = frame.querySelector('#h-skyMap-title');
      if (titleEl) {
        const titleText = titleEl.textContent.trim();
        const titleStyles = window.getComputedStyle(titleEl);
        const titlePos = getRelativePosition(titleEl);

        ctx.fillStyle = titleStyles.color || textColor;
        ctx.font = titleStyles.font || 'bold 24px serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        ctx.fillText(titleText, titlePos.x + titlePos.width / 2, titlePos.y);
        console.log('3D Mockup: Title drawn:', titleText);
      }

      // 4. Draw moons at their actual DOM positions
      const moonCanvases = frame.querySelectorAll('canvas[id^="h-moon-canvas"]');

      for (let i = 0; i < moonCanvases.length; i++) {
        const moonCanvas = moonCanvases[i];
        const moonPos = getRelativePosition(moonCanvas);

        // Force render the moon
        const moonNum = moonCanvas.id.replace('h-moon-canvas-', '');
        if (window.hMoonInstances && window.hMoonInstances[moonNum]) {
          const instance = window.hMoonInstances[moonNum];
          if (instance.initialized && instance.renderer && instance.scene && instance.camera) {
            instance.renderer.render(instance.scene, instance.camera);
          }
        }

        // Draw moon canvas at its actual position
        try {
          ctx.drawImage(moonCanvas, moonPos.x, moonPos.y, moonPos.width, moonPos.height);
          console.log('3D Mockup: Moon', moonNum, 'drawn at', moonPos.x, moonPos.y, 'size', moonPos.width);
        } catch (e) {
          console.warn('3D Mockup: Could not draw moon', moonNum, e);
        }
      }

      // 5. Draw moon labels (name, date, location) - use correct selectors
      const moonTextSelectors = '.h-moon-name-text, .h-moon-date-text, .h-moon-location-text';
      const moonTextElements = frame.querySelectorAll(moonTextSelectors);
      moonTextElements.forEach(function(textEl) {
        const text = textEl.textContent.trim();
        if (!text) return;

        const textPos = getRelativePosition(textEl);
        const textStyles = window.getComputedStyle(textEl);

        ctx.fillStyle = textStyles.color || textColor;
        ctx.font = textStyles.font || '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        ctx.fillText(text, textPos.x + textPos.width / 2, textPos.y);
        console.log('3D Mockup: Text drawn:', text);
      });

      // 6. Draw subtitle - #h-skyMap-subtitle
      const subtitleEl = frame.querySelector('#h-skyMap-subtitle');
      if (subtitleEl) {
        const subtitleText = subtitleEl.textContent.trim();
        const subtitleStyles = window.getComputedStyle(subtitleEl);
        const subtitlePos = getRelativePosition(subtitleEl);

        ctx.fillStyle = subtitleStyles.color || textColor;
        ctx.font = subtitleStyles.font || '14px serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        ctx.fillText(subtitleText, subtitlePos.x + subtitlePos.width / 2, subtitlePos.y);
        console.log('3D Mockup: Subtitle drawn:', subtitleText);
      }

      console.log('3D Mockup: Manual canvas complete');

      // Create Three.js texture from final composited canvas
      const texture = new THREE.CanvasTexture(finalCanvas);
      texture.needsUpdate = true;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.colorSpace = THREE.SRGBColorSpace;

      // Create gallery wrap edge textures from the edges of the canvas
      const edgeSize = Math.floor(finalCanvas.width * CONFIG.wrapEdgeSize);
      const edgeTextures = {};

      // Right edge - extract rightmost strip
      const rightEdgeCanvas = document.createElement('canvas');
      rightEdgeCanvas.width = edgeSize;
      rightEdgeCanvas.height = finalCanvas.height;
      const rightCtx = rightEdgeCanvas.getContext('2d');
      rightCtx.drawImage(finalCanvas,
        finalCanvas.width - edgeSize, 0, edgeSize, finalCanvas.height,
        0, 0, edgeSize, finalCanvas.height);
      edgeTextures.right = new THREE.CanvasTexture(rightEdgeCanvas);
      edgeTextures.right.colorSpace = THREE.SRGBColorSpace;

      // Left edge - extract leftmost strip
      const leftEdgeCanvas = document.createElement('canvas');
      leftEdgeCanvas.width = edgeSize;
      leftEdgeCanvas.height = finalCanvas.height;
      const leftCtx = leftEdgeCanvas.getContext('2d');
      leftCtx.drawImage(finalCanvas,
        0, 0, edgeSize, finalCanvas.height,
        0, 0, edgeSize, finalCanvas.height);
      edgeTextures.left = new THREE.CanvasTexture(leftEdgeCanvas);
      edgeTextures.left.colorSpace = THREE.SRGBColorSpace;

      // Top edge - extract topmost strip
      const topEdgeCanvas = document.createElement('canvas');
      topEdgeCanvas.width = finalCanvas.width;
      topEdgeCanvas.height = edgeSize;
      const topCtx = topEdgeCanvas.getContext('2d');
      topCtx.drawImage(finalCanvas,
        0, 0, finalCanvas.width, edgeSize,
        0, 0, finalCanvas.width, edgeSize);
      edgeTextures.top = new THREE.CanvasTexture(topEdgeCanvas);
      edgeTextures.top.colorSpace = THREE.SRGBColorSpace;

      // Bottom edge - extract bottommost strip
      const bottomEdgeCanvas = document.createElement('canvas');
      bottomEdgeCanvas.width = finalCanvas.width;
      bottomEdgeCanvas.height = edgeSize;
      const bottomCtx = bottomEdgeCanvas.getContext('2d');
      bottomCtx.drawImage(finalCanvas,
        0, finalCanvas.height - edgeSize, finalCanvas.width, edgeSize,
        0, 0, finalCanvas.width, edgeSize);
      edgeTextures.bottom = new THREE.CanvasTexture(bottomEdgeCanvas);
      edgeTextures.bottom.colorSpace = THREE.SRGBColorSpace;

      console.log('3D Mockup: Gallery wrap edge textures created');

      // Recreate the canvas frame with new textures (includes gallery wrap)
      createCanvasFrame(texture, edgeTextures);

      // Also update existing mesh materials if it exists
      if (mockupState.canvasMesh && mockupState.canvasMesh.material) {
        const materials = mockupState.canvasMesh.material;
        if (Array.isArray(materials) && materials[4]) {
          materials[4].map = texture;
          materials[4].color.set(0xffffff);
          materials[4].needsUpdate = true;
          console.log('3D Mockup: Texture applied to front face material');
        }
      }

      console.log('3D Mockup: Texture updated successfully');

    } catch (error) {
      console.error('Error capturing preview for 3D mockup:', error);
      // Ensure we clean up even on error
      frame.classList.remove('h-capturing');
    }
  }

  /**
   * Load html2canvas library dynamically
   */
  function loadHtml2Canvas() {
    return new Promise((resolve, reject) => {
      if (typeof html2canvas !== 'undefined') {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Load dom-to-image library dynamically
   */
  function loadDomToImage() {
    return new Promise((resolve, reject) => {
      if (typeof domtoimage !== 'undefined') {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/dom-to-image/2.6.0/dom-to-image.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Add mouse/touch event listeners for interaction
   */
  function addEventListeners(canvas) {
    // Mouse events
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', onMouseUp);

    // Touch events
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd);

    // Scroll wheel for zoom
    canvas.addEventListener('wheel', onWheel, { passive: false });

    // Double click to reset rotation
    canvas.addEventListener('dblclick', resetRotation);

    // Window resize
    window.addEventListener('resize', onWindowResize);
  }

  function onMouseDown(event) {
    mockupState.isDragging = true;
    mockupState.autoRotate = false;
    mockupState.previousMouseX = event.clientX;
    mockupState.previousMouseY = event.clientY;
  }

  function onMouseMove(event) {
    if (!mockupState.isDragging) return;

    const deltaX = event.clientX - mockupState.previousMouseX;
    const deltaY = event.clientY - mockupState.previousMouseY;

    mockupState.targetRotationY += deltaX * CONFIG.dragSensitivity;
    mockupState.targetRotationX += deltaY * CONFIG.dragSensitivity;

    // Clamp X rotation
    mockupState.targetRotationX = Math.max(-0.5, Math.min(0.5, mockupState.targetRotationX));

    mockupState.previousMouseX = event.clientX;
    mockupState.previousMouseY = event.clientY;
  }

  function onMouseUp() {
    mockupState.isDragging = false;
  }

  // Helper to calculate distance between two touch points
  function getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function onTouchStart(event) {
    event.preventDefault();
    mockupState.autoRotate = false;

    if (event.touches.length === 1) {
      // Single finger - rotate
      mockupState.isDragging = true;
      mockupState.isPinching = false;
      mockupState.previousMouseX = event.touches[0].clientX;
      mockupState.previousMouseY = event.touches[0].clientY;
    } else if (event.touches.length === 2) {
      // Two fingers - pinch to zoom
      mockupState.isDragging = false;
      mockupState.isPinching = true;
      mockupState.initialPinchDistance = getTouchDistance(event.touches);
    }
  }

  function onTouchMove(event) {
    event.preventDefault();

    if (mockupState.isDragging && event.touches.length === 1) {
      // Single finger rotation
      const deltaX = event.touches[0].clientX - mockupState.previousMouseX;
      const deltaY = event.touches[0].clientY - mockupState.previousMouseY;

      mockupState.targetRotationY += deltaX * CONFIG.dragSensitivity;
      mockupState.targetRotationX += deltaY * CONFIG.dragSensitivity;

      mockupState.targetRotationX = Math.max(-0.5, Math.min(0.5, mockupState.targetRotationX));

      mockupState.previousMouseX = event.touches[0].clientX;
      mockupState.previousMouseY = event.touches[0].clientY;
    } else if (mockupState.isPinching && event.touches.length === 2) {
      // Pinch to zoom
      const currentDistance = getTouchDistance(event.touches);
      const pinchDelta = mockupState.initialPinchDistance - currentDistance;

      // Convert pinch distance to zoom (pinch in = zoom out, pinch out = zoom in)
      mockupState.targetZoom += pinchDelta * 0.005;
      mockupState.targetZoom = Math.max(CONFIG.minZoom, Math.min(CONFIG.maxZoom, mockupState.targetZoom));

      mockupState.initialPinchDistance = currentDistance;
    }
  }

  function onTouchEnd(event) {
    if (event.touches.length === 0) {
      mockupState.isDragging = false;
      mockupState.isPinching = false;
    } else if (event.touches.length === 1) {
      // Switched from pinch to single finger
      mockupState.isPinching = false;
      mockupState.isDragging = true;
      mockupState.previousMouseX = event.touches[0].clientX;
      mockupState.previousMouseY = event.touches[0].clientY;
    }
  }

  function onWheel(event) {
    event.preventDefault();

    // Normalize wheel delta across browsers
    const delta = event.deltaY > 0 ? 1 : -1;

    // Update target zoom (positive delta = scroll down = zoom out)
    mockupState.targetZoom += delta * CONFIG.zoomSpeed;

    // Clamp to min/max zoom limits
    mockupState.targetZoom = Math.max(CONFIG.minZoom, Math.min(CONFIG.maxZoom, mockupState.targetZoom));
  }

  function resetRotation() {
    mockupState.targetRotationY = CONFIG.initialRotationY;
    mockupState.targetRotationX = CONFIG.initialRotationX;
    mockupState.targetZoom = CONFIG.initialZoom;
    mockupState.autoRotate = true;
  }

  function onWindowResize() {
    if (!mockupState.isActive) return;

    const container = document.getElementById('h-3d-mockup-container');
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    mockupState.camera.aspect = width / height;
    mockupState.camera.updateProjectionMatrix();
    mockupState.renderer.setSize(width, height);
  }

  /**
   * Animation loop
   */
  function animate() {
    if (!mockupState.isActive) return;

    mockupState.animationId = requestAnimationFrame(animate);

    // Auto-rotate if enabled
    if (mockupState.autoRotate && !mockupState.isDragging) {
      mockupState.targetRotationY += CONFIG.rotationSpeed;
    }

    // Smooth interpolation for rotation
    if (mockupState.frameGroup) {
      mockupState.frameGroup.rotation.y += (mockupState.targetRotationY - mockupState.frameGroup.rotation.y) * 0.1;
      mockupState.frameGroup.rotation.x += (mockupState.targetRotationX - mockupState.frameGroup.rotation.x) * 0.1;
    }

    // Smooth interpolation for zoom (camera distance)
    if (mockupState.camera) {
      mockupState.currentZoom += (mockupState.targetZoom - mockupState.currentZoom) * 0.1;
      mockupState.camera.position.z = mockupState.currentZoom;
    }

    mockupState.renderer.render(mockupState.scene, mockupState.camera);
  }

  /**
   * Debounced texture update - prevents excessive re-renders
   */
  function debouncedTextureUpdate() {
    if (mockupState.updateDebounceTimer) {
      clearTimeout(mockupState.updateDebounceTimer);
    }
    mockupState.updateDebounceTimer = setTimeout(async () => {
      if (mockupState.isActive) {
        console.log('3D Mockup: Live sync - updating texture');
        await captureAndUpdateTexture();
      }
    }, 150); // 150ms debounce
  }

  /**
   * Start observing for live changes in the preview
   */
  function startLiveSync() {
    const frame = document.querySelector('.h-custom-frame');
    if (!frame) return;

    // 1. MutationObserver for DOM changes (text content, attributes, child elements)
    mockupState.mutationObserver = new MutationObserver((mutations) => {
      // Filter out irrelevant mutations
      const relevantMutation = mutations.some(mutation => {
        // Ignore mutations from the 3D mockup container itself
        if (mutation.target.closest && mutation.target.closest('#h-3d-mockup-container')) {
          return false;
        }
        return true;
      });

      if (relevantMutation) {
        debouncedTextureUpdate();
      }
    });

    mockupState.mutationObserver.observe(frame, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'data-background', 'src']
    });

    // 2. Input event listeners for text fields and selects
    const inputSelectors = [
      '#h-skyMap-title-input',
      '#h-skyMap-subtitle-input',
      '.h-moon-name-input',
      '.h-moon-date-input',
      '.h-moon-location-input',
      'input[type="text"]',
      'input[type="date"]',
      'textarea',
      'select'
    ];

    const appContainer = document.querySelector('.h-app');
    if (appContainer) {
      inputSelectors.forEach(selector => {
        const elements = appContainer.querySelectorAll(selector);
        elements.forEach(el => {
          const handler = () => debouncedTextureUpdate();
          el.addEventListener('input', handler);
          el.addEventListener('change', handler);
          mockupState.inputListeners.push({ element: el, handler });
        });
      });
    }

    // 3. Listen for custom events that the app might dispatch
    const customEventHandler = () => debouncedTextureUpdate();
    document.addEventListener('h-moon-updated', customEventHandler);
    document.addEventListener('h-background-changed', customEventHandler);
    document.addEventListener('h-preview-updated', customEventHandler);
    mockupState.inputListeners.push(
      { element: document, event: 'h-moon-updated', handler: customEventHandler },
      { element: document, event: 'h-background-changed', handler: customEventHandler },
      { element: document, event: 'h-preview-updated', handler: customEventHandler }
    );

    // 4. Watch for background changes via clicks on background options
    const bgOptions = document.querySelectorAll('.h-background-option, [data-background]');
    bgOptions.forEach(el => {
      const handler = () => {
        // Longer delay to allow background image to fully load and apply
        setTimeout(debouncedTextureUpdate, 300);
      };
      el.addEventListener('click', handler);
      mockupState.inputListeners.push({ element: el, event: 'click', handler });
    });

    // 5. Watch for background style changes directly on the frame
    const frameElement = document.querySelector('.h-custom-frame');
    if (frameElement) {
      let lastBackgroundImage = window.getComputedStyle(frameElement).backgroundImage;

      // Check for background changes periodically when active
      const bgCheckInterval = setInterval(() => {
        if (!mockupState.isActive) {
          clearInterval(bgCheckInterval);
          return;
        }
        const currentBg = window.getComputedStyle(frameElement).backgroundImage;
        if (currentBg !== lastBackgroundImage) {
          console.log('3D Mockup: Background change detected');
          lastBackgroundImage = currentBg;
          // Wait a bit for the image to fully load
          setTimeout(debouncedTextureUpdate, 200);
        }
      }, 100);

      mockupState.inputListeners.push({
        element: null,
        event: 'interval',
        handler: bgCheckInterval
      });
    }

    console.log('3D Mockup: Live sync started');
  }

  /**
   * Stop observing for live changes
   */
  function stopLiveSync() {
    // Disconnect mutation observer
    if (mockupState.mutationObserver) {
      mockupState.mutationObserver.disconnect();
      mockupState.mutationObserver = null;
    }

    // Remove input event listeners and clear intervals
    mockupState.inputListeners.forEach(({ element, event, handler }) => {
      if (event === 'interval') {
        // Clear interval timers
        clearInterval(handler);
      } else if (element) {
        const eventType = event || 'input';
        element.removeEventListener(eventType, handler);
        if (eventType === 'input') {
          element.removeEventListener('change', handler);
        }
      }
    });
    mockupState.inputListeners = [];

    // Clear any pending debounce timer
    if (mockupState.updateDebounceTimer) {
      clearTimeout(mockupState.updateDebounceTimer);
      mockupState.updateDebounceTimer = null;
    }

    console.log('3D Mockup: Live sync stopped');
  }

  /**
   * Show the 3D mockup view
   */
  async function show3DMockup() {
    const container = document.getElementById('h-3d-mockup-container');
    const printArea = document.getElementById('h-print');

    if (!container || !printArea) {
      console.error('Required elements not found');
      return;
    }

    // Hide the flat preview
    printArea.classList.add('h-hidden-for-3d');

    // Show 3D container
    container.classList.add('h-3d-active');

    mockupState.isActive = true;

    // Capture current preview and update texture
    await captureAndUpdateTexture();

    // Resize to fit container
    onWindowResize();

    // Start animation
    animate();

    // Start live sync to update texture when preview changes
    startLiveSync();

    console.log('3D Mockup view activated');
  }

  /**
   * Hide the 3D mockup view
   */
  function hide3DMockup() {
    const container = document.getElementById('h-3d-mockup-container');
    const printArea = document.getElementById('h-print');

    if (container) {
      container.classList.remove('h-3d-active');
    }

    if (printArea) {
      printArea.classList.remove('h-hidden-for-3d');
    }

    mockupState.isActive = false;

    // Stop live sync
    stopLiveSync();

    // Stop animation
    if (mockupState.animationId) {
      cancelAnimationFrame(mockupState.animationId);
      mockupState.animationId = null;
    }

    console.log('3D Mockup view deactivated');
  }

  /**
   * Toggle 3D mockup view
   */
  function toggle3DMockup() {
    if (mockupState.isActive) {
      hide3DMockup();
    } else {
      show3DMockup();
    }
    return mockupState.isActive;
  }

  /**
   * Update the 3D mockup (call when preview changes)
   */
  async function update3DMockup() {
    if (mockupState.isActive) {
      await captureAndUpdateTexture();
    }
  }

  /**
   * Create the toggle button
   */
  function createToggleButton() {
    const existingBtn = document.getElementById('h-3d-toggle-btn');
    if (existingBtn) return;

    const btn = document.createElement('button');
    btn.id = 'h-3d-toggle-btn';
    btn.className = 'h-3d-toggle-btn';
    btn.innerHTML = `
      <svg class="h-3d-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
        <path d="M2 17l10 5 10-5"/>
        <path d="M2 12l10 5 10-5"/>
      </svg>
      <span class="h-3d-label">3D View</span>
    `;
    btn.title = 'Toggle 3D Canvas Preview';

    btn.addEventListener('click', function() {
      const isActive = toggle3DMockup();
      btn.classList.toggle('active', isActive);
      btn.querySelector('.h-3d-label').textContent = isActive ? 'Flat View' : '3D View';
    });

    // Insert button in the preview area
    const printArea = document.getElementById('h-print');
    if (printArea && printArea.parentNode) {
      printArea.parentNode.insertBefore(btn, printArea);
    }
  }

  // Initialize when DOM is ready
  function initWhenReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(initAll, 500); // Wait for other scripts
      });
    } else {
      setTimeout(initAll, 500);
    }
  }

  function initAll() {
    init3DMockup();
    createToggleButton();
  }

  // Expose global functions
  window.HMoon3DMockup = {
    init: init3DMockup,
    show: show3DMockup,
    hide: hide3DMockup,
    toggle: toggle3DMockup,
    update: update3DMockup,
    isActive: function() { return mockupState.isActive; }
  };

  // Auto-initialize
  initWhenReady();

})();
